import { Link } from "@tanstack/react-router";
import {
  hijriToGregorian,
  daysInHijriMonth,
  HIJRI_MONTHS,
  HIJRI_MONTHS_EN,
} from "@/lib/calendar/hijri";
import { dayOfWeek, dateSlug, MONTH_NAMES_ID, calendarDatePath } from "@/lib/calendar/dates";
import { getHolidaysForDate } from "@/data/holidays";
import { getHijriEvent } from "@/lib/calendar/hijri-events";
import { useLocale } from "@/i18n/LocaleContext";
import { cn } from "@/lib/utils";

interface Props {
  hYear: number;
  hMonth: number; // 1-12
  compact?: boolean;
  /** "fixed" pads to 6 weeks; "auto" trims trailing empty week rows. */
  align?: "auto" | "fixed";
  /** Optional month-level URL; rendered on the month header to avoid nested date links. */
  monthHref?: string;
}

export function HijriMonthGrid({ hYear, hMonth, compact, align = "fixed", monthHref }: Props) {
  const { t, href, locale } = useLocale();
  const monthName = (locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS)[hMonth - 1];
  const total = daysInHijriMonth(hYear, hMonth);
  const first = hijriToGregorian(hYear, hMonth, 1);
  const firstDow = dayOfWeek(first.y, first.m, first.d);
  const daysShort = t.common.daysShort;
  const monthsLong = locale === "en"
    ? ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
    : MONTH_NAMES_ID.map(n => n.slice(0,3));

  const cells: Array<{ hd: number; g: { y: number; m: number; d: number } } | null> = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= total; d++) {
    cells.push({ hd: d, g: hijriToGregorian(hYear, hMonth, d) });
  }
  if (align === "fixed") {
    while (cells.length < 42) cells.push(null);
  } else {
    while (cells.length % 7 !== 0) cells.push(null);
  }

  return (
    <div className={cn("rounded-lg border border-border bg-card overflow-hidden", compact && "text-xs")}>
      {monthHref ? (
        <Link
          to={monthHref as never}
          className="block border-b border-border bg-card px-3 py-2 text-sm font-semibold transition-colors hover:bg-accent hover:text-primary"
          aria-label={`${monthName} ${hYear} ${locale === "en" ? "AH" : "H"}`}
        >
          {monthName} {hYear} {locale === "en" ? "AH" : "H"}
        </Link>
      ) : (
        <div className="border-b border-border bg-card px-3 py-2 text-sm font-semibold">
          {monthName} {hYear} {locale === "en" ? "AH" : "H"}
        </div>
      )}
      <div className="grid grid-cols-7 bg-muted/40">
        {daysShort.map((d, i) => (
          <div
            key={d}
            className={cn(
              "px-1 py-1.5 text-center text-[10px] font-medium uppercase tracking-wide",
              i === 0 ? "text-red-date" : "text-muted-foreground",
            )}
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid auto-rows-fr grid-cols-7">
        {cells.map((cell, i) => {
          if (!cell) return <div key={i} className={cn(compact ? "h-8" : "min-h-20 sm:min-h-24", "border-t border-border")} />;
          const dow = (firstDow + cell.hd - 1) % 7;
          const iso = `${cell.g.y}-${String(cell.g.m).padStart(2,"0")}-${String(cell.g.d).padStart(2,"0")}`;
          const hols = getHolidaysForDate(iso);
          const isLibur = hols.some(h => h.type === "libur");
          const isRed = isLibur || dow === 0;
          const ev = getHijriEvent(hMonth, cell.hd);
          const isBigEvent = ev?.type === "hari-besar";
          const evName = ev ? (locale === "en" ? ev.nameEn : ev.name) : null;
          return (
            <Link
              key={i}
              to={href(calendarDatePath(cell.g.y, cell.g.m, cell.g.d)) as never}
              className={cn(
                "group relative block border-t border-l border-border first:border-l-0 [&:nth-child(7n+1)]:border-l-0",
                "p-1.5 transition-colors hover:bg-accent/60",
                compact ? "h-8 text-center" : "min-h-20 sm:min-h-24",
                isRed && "bg-red-date-bg/40",
                !isRed && isBigEvent && "bg-emerald-50/60 dark:bg-emerald-950/30",
                !isRed && ev && !isBigEvent && "bg-amber-50/50 dark:bg-amber-950/20",
              )}
              title={isLibur ? hols[0].name : evName ?? undefined}
            >
              <div className="flex items-baseline justify-between">
                <span className={cn("text-base font-semibold leading-none", isRed && "text-red-date")}>
                  {cell.hd}
                </span>
                {!compact && (
                  <span className="text-[9px] text-muted-foreground">
                    {cell.g.d} {monthsLong[cell.g.m - 1]}
                  </span>
                )}
              </div>
              {!compact && isLibur && (
                <div className="mt-1 break-words text-[10px] sm:text-[11px] font-medium leading-tight text-red-date">
                  {hols[0].name}
                </div>
              )}
              {!compact && !isLibur && evName && (
                <div className={cn(
                  "mt-1 break-words text-[10px] sm:text-[11px] font-medium leading-tight",
                  isBigEvent ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300",
                )}>
                  {evName}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}